import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { usePerson, useDeletePerson } from '../api/person';
import { PersonForm } from '../components/person/PersonForm';
import { DeleteConfirmDialog } from '../components/person/DeleteConfirmDialog';
import { ArrowLeft, Edit, Trash2, Users, Heart, ChevronRight } from 'lucide-react';
import type { PersonSummary, CreatePersonDto } from '../types/api';
import { useUpdatePerson, useCreatePerson } from '../api/person';

export function PersonPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: person, isLoading, error } = usePerson(id || '');

    // Modal states
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isAddChildFormOpen, setIsAddChildFormOpen] = useState(false);

    // Mutations
    const updatePerson = useUpdatePerson();
    const createPerson = useCreatePerson();
    const deletePerson = useDeletePerson();

    const handleEditSubmit = (data: CreatePersonDto) => {
        if (!person) return;
        updatePerson.mutate({ id: person.id, dto: data }, {
            onSuccess: () => setIsEditFormOpen(false),
        });
    };

    const handleAddChildSubmit = (data: CreatePersonDto) => {
        if (!person) return;
        createPerson.mutate({
            ...data,
            parentIds: [person.id],
        }, {
            onSuccess: () => setIsAddChildFormOpen(false),
        });
    };

    const handleConfirmDelete = () => {
        if (!person) return;
        deletePerson.mutate(person.id, {
            onSuccess: () => {
                setIsDeleteDialogOpen(false);
                navigate('/tree');
            },
        });
    };

    const formatDate = (date: string | null) => {
        if (!date) return 'Chưa rõ';
        return new Date(date).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getGenderLabel = (gender: string) => {
        switch (gender) {
            case 'MALE': return '👨 Nam';
            case 'FEMALE': return '👩 Nữ';
            default: return '🧑 Khác';
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 text-lg">Đang tải thông tin...</p>
                </div>
            </div>
        );
    }

    if (error || !person) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 text-lg mb-4">Không tìm thấy thông tin người này</p>
                    <Link to="/tree" className="btn btn-primary">
                        ← Quay lại cây gia phả
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 py-4">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/tree')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Quay lại cây gia phả"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="flex-1">
                            <h1 className="text-2xl font-bold text-gray-900">{person.fullName}</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {getGenderLabel(person.gender)} • {formatDate(person.birthDate)}
                                {person.deathDate && ` — ${formatDate(person.deathDate)}`}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setIsEditFormOpen(true)}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                <Edit className="w-4 h-4" />
                                Sửa
                            </button>
                            <button
                                onClick={() => setIsDeleteDialogOpen(true)}
                                className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                Xóa
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
                {/* Photo + Basic Info */}
                <div className="card">
                    <div className="flex gap-6">
                        {person.photoUrl ? (
                            <img
                                src={person.photoUrl}
                                alt={person.fullName}
                                className="w-32 h-32 rounded-xl object-cover border-2 border-gray-100"
                            />
                        ) : (
                            <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center border-2 border-primary-50">
                                <span className="text-4xl font-bold text-primary-600">
                                    {person.fullName.charAt(0)}
                                </span>
                            </div>
                        )}
                        <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Họ tên đầy đủ</label>
                                    <p className="text-gray-900 font-medium">{person.fullName}</p>
                                </div>
                                {person.givenName && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Tên gọi</label>
                                        <p className="text-gray-900 font-medium">{person.givenName}</p>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Giới tính</label>
                                    <p className="text-gray-900">{getGenderLabel(person.gender)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Ngày sinh</label>
                                    <p className="text-gray-900">{formatDate(person.birthDate)}</p>
                                </div>
                            </div>
                            {person.deathDate && (
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Ngày mất</label>
                                    <p className="text-gray-900">{formatDate(person.deathDate)}</p>
                                </div>
                            )}
                        </div>
                    </div>
                    {person.notes && (
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <label className="text-sm font-medium text-gray-500">Ghi chú</label>
                            <p className="mt-1 text-gray-700 whitespace-pre-wrap">{person.notes}</p>
                        </div>
                    )}
                </div>

                {/* Family Relationships */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Parents */}
                    <RelationshipCard
                        title="Cha / Mẹ"
                        icon={<Users className="w-5 h-5" />}
                        people={person.parents}
                        emptyText="Chưa có thông tin"
                    />

                    {/* Spouses */}
                    <RelationshipCard
                        title="Vợ / Chồng"
                        icon={<Heart className="w-5 h-5" />}
                        people={person.spouses}
                        emptyText="Chưa có thông tin"
                    />

                    {/* Children */}
                    <div className="card">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-gray-700">
                                <Users className="w-5 h-5" />
                                <h3 className="font-semibold">Con cái</h3>
                            </div>
                            <button
                                onClick={() => setIsAddChildFormOpen(true)}
                                className="text-xs px-3 py-1 bg-primary-50 text-primary-700 rounded-full hover:bg-primary-100 transition-colors font-medium"
                            >
                                + Thêm con
                            </button>
                        </div>
                        {person.children.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">Chưa có thông tin</p>
                        ) : (
                            <ul className="space-y-2">
                                {person.children.map((child) => (
                                    <PersonLink key={child.id} person={child} />
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Form Modal */}
            <PersonForm
                isOpen={isEditFormOpen}
                onClose={() => setIsEditFormOpen(false)}
                onSubmit={handleEditSubmit}
                person={person}
                isLoading={updatePerson.isPending}
            />

            {/* Add Child Form Modal */}
            <PersonForm
                isOpen={isAddChildFormOpen}
                onClose={() => setIsAddChildFormOpen(false)}
                onSubmit={handleAddChildSubmit}
                isLoading={createPerson.isPending}
            />

            {/* Delete Confirmation */}
            <DeleteConfirmDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                personName={person.fullName}
                isLoading={deletePerson.isPending}
            />
        </div>
    );
}

// Reusable relationship card component
function RelationshipCard({
    title,
    icon,
    people,
    emptyText,
}: {
    title: string;
    icon: React.ReactNode;
    people: PersonSummary[];
    emptyText: string;
}) {
    return (
        <div className="card">
            <div className="flex items-center gap-2 mb-3 text-gray-700">
                {icon}
                <h3 className="font-semibold">{title}</h3>
            </div>
            {people.length === 0 ? (
                <p className="text-sm text-gray-400 italic">{emptyText}</p>
            ) : (
                <ul className="space-y-2">
                    {people.map((person) => (
                        <PersonLink key={person.id} person={person} />
                    ))}
                </ul>
            )}
        </div>
    );
}

// Reusable person link component
function PersonLink({ person }: { person: PersonSummary }) {
    return (
        <li>
            <Link
                to={`/person/${person.id}`}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
            >
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-sm font-semibold">
                    {person.fullName.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-primary-600">
                        {person.fullName}
                    </p>
                    {(person.birthDate || person.deathDate) && (
                        <p className="text-xs text-gray-400">
                            {person.birthDate ? new Date(person.birthDate).getFullYear() : '?'}
                            {person.deathDate ? ` — ${new Date(person.deathDate).getFullYear()}` : ''}
                        </p>
                    )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary-400" />
            </Link>
        </li>
    );
}
